import {Box, Drawer, DrawerProps, Stack, Typography} from '@mui/material';
import {MainCard} from 'components/Theme';
import {AppTooltip} from 'components/toolip';
import {RIGHT_DRAWER_WIDTH, RIGHT_MINI_DRAWER_WIDTH} from 'configs';
import {useAppDrawerInRedux} from 'hooks';
import {CloseCircle} from 'iconsax-react';
import {forwardRef, ReactNode, useEffect, useImperativeHandle, useState} from 'react';
import {BsFillPinFill, BsPinAngle} from 'react-icons/bs';
import {useSelector} from 'react-redux';
import {appDrawerSelector} from 'store/themeSettings';
import {DrawerTypeValue} from 'types';
import SimpleBar from './SimpleBar';

export interface AppDrawerRefType {
    handleOpen: () => void;
    handleClose: () => void;
    open: boolean;
}
interface AppDrawerProps extends Omit<DrawerProps, 'open' | 'onClose'> {
    children: ReactNode;
    title?: string;
    zIndex?: number;
    showHeaderButton?: boolean;
    drawerType?: DrawerTypeValue | null;
    renderRightHeader?: ReactNode;
    nodeBeforeTitle?: ReactNode;
    renderElement: (open: boolean, handleOpen: () => void) => ReactNode;
    onCloseClickHandler?: () => void;
}

export const AppDrawer = forwardRef<AppDrawerRefType, AppDrawerProps>(
    (
        {
            children,
            title,
            renderRightHeader,
            nodeBeforeTitle,
            zIndex = 1200,
            showHeaderButton,
            drawerType,
            renderElement,
            onCloseClickHandler,
            ...rest
        },
        ref,
    ) => {
        const {setIsRightDrawerOpenInRedux, setIsRightDrawerPinnedInRedux} = useAppDrawerInRedux();
        const {isRightDrawerOpened, isRightDrawerPinned} = useSelector(appDrawerSelector);
        const [open, setOpen] = useState(false);
        const handleOpen = () => {
            if (drawerType) setIsRightDrawerOpenInRedux(drawerType);
            setOpen(true);
        };
        const handleClose = () => {
            if (drawerType) {
                setIsRightDrawerOpenInRedux(null);
            }
            setOpen(false);
            if (onCloseClickHandler) {
                onCloseClickHandler();
            }
        };

        const handlePinned = () => {
            setIsRightDrawerPinnedInRedux(!isRightDrawerPinned);
        };

        useEffect(() => {
            if (drawerType) {
                if (drawerType === isRightDrawerOpened) {
                    setOpen(true);
                } else {
                    setOpen(false);
                }
            }
        }, [isRightDrawerOpened, drawerType]);

        useImperativeHandle(ref, () => ({
            open,
            handleOpen,
            handleClose,
        }));

        return (
            <>
                {renderElement(open, handleOpen)}
                {open && (
                    <Drawer
                        sx={{
                            position: 'fixed',
                            right: 0,
                            zIndex: zIndex,
                            width: RIGHT_DRAWER_WIDTH - RIGHT_MINI_DRAWER_WIDTH,
                            marginRight: `${RIGHT_MINI_DRAWER_WIDTH + 10}px`,

                            '& .MuiDrawer-paper': {
                                width: RIGHT_DRAWER_WIDTH - RIGHT_MINI_DRAWER_WIDTH,
                                border: 'none',
                                marginRight: `${RIGHT_MINI_DRAWER_WIDTH + 10}px`,
                                borderRadius: '0px',
                            },
                        }}
                        anchor="right"
                        open={open}
                        transitionDuration={0}
                        variant="persistent"
                        {...rest}
                        onClose={handleClose}
                    >
                        <MainCard
                            content={false}
                            sx={{border: 'none', borderRadius: 0, height: '100vh', overflow: 'hidden'}}
                        >
                            <Box p={1} px={1.5}>
                                <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
                                    {/* Left Section (title and optional nodeBeforeTitle) */}
                                    <Stack direction="row" alignItems="center" spacing={1}>
                                        {nodeBeforeTitle}
                                        {title && <Typography variant="h5">{title}</Typography>}
                                        {renderRightHeader}
                                    </Stack>

                                    {/* Right Section (pin and close buttons) */}
                                    <Stack direction="row" alignItems="start" spacing={0.5}>
                                        <AppTooltip title={`${isRightDrawerPinned ? 'Unpin' : 'Pin'} Drawer`}>
                                            <Box
                                                onClick={handlePinned}
                                                sx={{
                                                    display: 'inline-flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    p: 0.5,
                                                    mt: 0.1,
                                                    borderRadius: '20%',
                                                    bgcolor: 'transparent',
                                                    color: 'primary.main',
                                                    cursor: 'pointer',
                                                    // transition: 'all 0.2s ease-in-out',
                                                    '&:hover': {
                                                        bgcolor: 'secondary.light',
                                                        // boxShadow: 1, // MUI elevation
                                                        // transform: 'scale(1.05)',
                                                    },
                                                }}
                                            >
                                                {isRightDrawerPinned ? (
                                                    <BsFillPinFill size={15} />
                                                ) : (
                                                    <BsPinAngle size={15} />
                                                )}
                                            </Box>
                                        </AppTooltip>

                                        <Box
                                            onClick={handleClose}
                                            sx={{
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                p: 0.5,
                                                mt: 2,
                                                color: 'error.main',
                                                cursor: 'pointer',
                                            }}
                                        >
                                            <CloseCircle size={18} />
                                        </Box>
                                    </Stack>
                                </Stack>
                            </Box>

                            <SimpleBar sx={{height: 'calc(100vh - 55px)', mt: 1, overflowY: 'hidden'}}>
                                <Box sx={{px: 1.5}}>{children}</Box>
                            </SimpleBar>
                        </MainCard>
                    </Drawer>
                )}
            </>
        );
    },
);

export default AppDrawer;
